"use client";

import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { Button, Skeleton, Slider, Textarea } from "@/ui";
import useRecruiterDetails from "@/lib/user/useRecruiterDetails";
import useCommunicationStyle, {
  type CommunicationStyleValues,
} from "@/lib/user/useCommunicationStyle";
import styles from "./CommunicationStylePanel.module.css";

const GUIDANCE_LIMIT = 1000;

const GUIDANCE_PLACEHOLDER =
  "Anything else about how you write. For example, words to avoid, how you sign off, or phrases you always use.";

/** How long the "Saved" confirmation stays up before fading back to idle. */
const SAVED_CONFIRMATION_MS = 3000;

/**
 * How long to wait after the last edit before persisting. Long enough to batch
 * a slider drag or a sentence of typing into one request, short enough that a
 * user who reads the "Saved" chip and closes the dialog isn't racing it.
 */
const AUTOSAVE_DEBOUNCE_MS = 800;

const EMPTY_VALUES: CommunicationStyleValues = {
  formality: 3,
  length: 3,
  energy: 3,
  vocabulary: 3,
  bluntness: 3,
  custom_guidance: "",
};

function CheckIcon() {
  return (
    <svg
      viewBox="0 0 16 16"
      width="14"
      height="14"
      fill="none"
      aria-hidden="true"
      className={styles.savedIcon}
    >
      <path
        d="M3.5 8.5l3 3 6-7"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * The recruiter's structured writing style controls.
 *
 * Five 1-5 dimensions plus optional free-text guidance, seeded during
 * onboarding from the description extracted from their website. These values
 * compose the base prompt server-side.
 *
 * Edits persist themselves shortly after the user stops making them, through
 * this panel's own endpoint. That matters because the surrounding modal's
 * "Looks great" doesn't carry these values, and "Review later" doesn't save at
 * all — without auto-save, an adjusted slider would be silently discarded by
 * either exit.
 */
export default function CommunicationStylePanel() {
  const { recruiter } = useRecruiterDetails();
  const { query, mutation } = useCommunicationStyle(recruiter?.uuid);

  const [values, setValues] = useState<CommunicationStyleValues>(EMPTY_VALUES);
  const [guidanceOpen, setGuidanceOpen] = useState(false);
  const [saved, setSaved] = useState(false);
  const savedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const autosaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // The edit waiting to be written, or null when everything is persisted. Held
  // in a ref (not state) so the unmount flush below can read the latest value
  // without re-subscribing the cleanup on every keystroke.
  const pending = useRef<CommunicationStyleValues | null>(null);

  const style = query.data;

  // Seed local state once the fetch lands. Adjusting during render (rather than
  // in an effect) avoids a throwaway pass showing the neutral defaults.
  const [seededFrom, setSeededFrom] = useState<unknown>(null);
  if (style && seededFrom !== style) {
    setSeededFrom(style);
    setValues({
      formality: style.formality,
      length: style.length,
      energy: style.energy,
      vocabulary: style.vocabulary,
      bluntness: style.bluntness,
      custom_guidance: style.custom_guidance ?? "",
    });
    // Already-written guidance is shown expanded so it is never hidden away.
    setGuidanceOpen(Boolean(style.custom_guidance));
  }

  const save = (next: CommunicationStyleValues) => {
    pending.current = null;
    mutation.mutate(next, {
      onSuccess: () => {
        setSaved(true);
        if (savedTimer.current) clearTimeout(savedTimer.current);
        savedTimer.current = setTimeout(
          () => setSaved(false),
          SAVED_CONFIRMATION_MS
        );
      },
      onError: (error: any) => {
        toast.error(
          error?.response?.data?.message || "Could not save your writing style"
        );
      },
    });
  };

  // Kept current so the debounce timer and the unmount flush always call the
  // latest closure without either of them depending on it.
  const saveRef = useRef(save);
  useEffect(() => {
    saveRef.current = save;
  });

  /** Record an edit and (re)start the debounce window. */
  const scheduleSave = (next: CommunicationStyleValues) => {
    pending.current = next;
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(
      () => saveRef.current(next),
      AUTOSAVE_DEBOUNCE_MS
    );
  };

  useEffect(
    () => () => {
      if (savedTimer.current) clearTimeout(savedTimer.current);
      if (autosaveTimer.current) clearTimeout(autosaveTimer.current);

      // Flush on unmount so an edit made in the last moment before the dialog
      // closes isn't lost to the debounce window. The mutation outlives this
      // component; only its setState callbacks become no-ops.
      if (pending.current) saveRef.current(pending.current);
    },
    []
  );

  const dimensions = style?.dimensions ?? {};
  const dirty =
    style != null &&
    (Object.keys(EMPTY_VALUES) as (keyof CommunicationStyleValues)[]).some(
      (key) => values[key] !== (style[key] ?? EMPTY_VALUES[key])
    );

  /** Apply an edit locally and queue it for saving. */
  const edit = (changes: Partial<CommunicationStyleValues>) => {
    const next = { ...values, ...changes };
    setSaved(false);
    setValues(next);
    scheduleSave(next);
  };

  const setBand = (dimension: string, band: number) => {
    edit({ [dimension]: band });
  };

  const setGuidance = (value: string) => {
    edit({ custom_guidance: value.slice(0, GUIDANCE_LIMIT) });
  };

  if (query.isLoading) {
    return (
      <div className={styles.panel}>
        <Skeleton height={20} />
        <Skeleton height={140} />
      </div>
    );
  }

  const guidanceLength = values.custom_guidance.length;

  return (
    <div className={styles.panel}>
      <p className={styles.intro}>
        We set these from your website. Adjust anything that doesn&apos;t sound
        like you — they shape how your outreach is written.
      </p>

      <div className={styles.dimensions}>
        {Object.entries(dimensions).map(([key, dimension]) => {
          const value = values[key as keyof CommunicationStyleValues] as number;
          const bandLabel =
            dimension.bands.find((b) => b.band === value)?.label ?? "";
          const first = dimension.bands[0];
          const last = dimension.bands[dimension.bands.length - 1];

          return (
            <div key={key} className={styles.dimension}>
              <div className={styles.dimensionHeader}>
                <span className={styles.dimensionName}>{dimension.label}</span>
                <span className={styles.bandLabel}>{bandLabel}</span>
              </div>

              <Slider
                min={first?.band ?? 1}
                max={last?.band ?? 5}
                step={1}
                marks
                value={value}
                onChange={(band) => setBand(key, band)}
                ariaLabel={dimension.label}
                getAriaValueText={(band) =>
                  dimension.bands.find((b) => b.band === band)?.label ??
                  String(band)
                }
              />

              <div className={styles.extremes}>
                <span>{first?.label}</span>
                <span>{last?.label}</span>
              </div>
            </div>
          );
        })}
      </div>

      <div className={styles.guidance}>
        {guidanceOpen ? (
          <>
            <Textarea
              label="Custom guidance"
              placeholder={GUIDANCE_PLACEHOLDER}
              value={values.custom_guidance}
              onChange={setGuidance}
              maxLength={GUIDANCE_LIMIT}
              rows={4}
            />
            <span
              className={`${styles.counter} ${
                guidanceLength > GUIDANCE_LIMIT * 0.9
                  ? styles.counterNearLimit
                  : ""
              }`}
            >
              {guidanceLength}/{GUIDANCE_LIMIT}
            </span>
          </>
        ) : (
          <Button
            variant="tertiary"
            noPadding
            className={styles.guidanceTrigger}
            onClick={() => setGuidanceOpen(true)}
          >
            + Add custom guidance
          </Button>
        )}
      </div>

      <div className={styles.actions} aria-live="polite">
        {mutation.isPending ? (
          <span className={styles.status}>Saving…</span>
        ) : saved && !dirty ? (
          <span className={styles.saved}>
            <CheckIcon />
            Saved
          </span>
        ) : null}
      </div>
    </div>
  );
}
